import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Share2, MessageCircle, Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import {
    buildLeadShareText,
    buildChatGPTPrompt,
    openWhatsAppShare,
    openChatGPTShare,
    copyLeadText,
} from "../../lib/leadShare";

/**
 * <LeadShareButton /> — compact dropdown that shares a demo lead through
 * WhatsApp Business, ChatGPT, or the OS clipboard.
 *
 * Props:
 *   - item: lead object (registration / inquiry / app-lead)
 *   - kind: "registrations" | "inquiries" | "app-leads" | "tutor-applications"
 *   - variant: "compact" (default header pill) | "iconOnly" (mobile FAB slot)
 *
 * Renders nothing when `item` is missing. Fully keyboard-accessible; closes
 * on outside click / Esc.
 *
 * Positioning:
 *   The menu uses `position: fixed` with viewport-clamped coordinates
 *   computed from the trigger's `getBoundingClientRect()`. This escapes any
 *   `overflow-hidden` parent (the LeadDetailModal chrome) AND guarantees
 *   the menu never crosses the viewport edges — regardless of whether the
 *   trigger sits on the left, right, or middle of the screen. When there
 *   isn't enough room below, the menu flips above the trigger.
 */

const MENU_WIDTH = 288;             // ~18rem — matches the visual target
const MIN_MENU_WIDTH = 240;         // squeeze on ultra-narrow (<280px) screens
const MENU_HEIGHT_ESTIMATE = 216;   // 3 rows × ~72px — used only for flip decision
const EDGE_MARGIN = 8;              // safe gap from any viewport edge

function _computePosition(triggerRect) {
    if (typeof window === "undefined" || !triggerRect) {
        return { left: 0, top: 0, width: MENU_WIDTH };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // On mobile widths, the menu can be as wide as the safe viewport
    const width = Math.max(MIN_MENU_WIDTH, Math.min(MENU_WIDTH, vw - EDGE_MARGIN * 2));

    // Horizontal: prefer aligning the menu's LEFT edge to the trigger's
    // left. Fall back to right-align if that would clip the right edge.
    // Final clamp keeps the menu at least EDGE_MARGIN away from both edges.
    let left = triggerRect.left;
    if (left + width + EDGE_MARGIN > vw) {
        left = triggerRect.right - width; // right-align to trigger
    }
    left = Math.max(EDGE_MARGIN, Math.min(left, vw - width - EDGE_MARGIN));

    // Vertical: open below by default (8px gap). If there's not enough
    // space below the trigger, flip above.
    const below = triggerRect.bottom + 8;
    const above = triggerRect.top - 8 - MENU_HEIGHT_ESTIMATE;
    const spaceBelow = vh - triggerRect.bottom - EDGE_MARGIN;
    let top;
    if (spaceBelow >= MENU_HEIGHT_ESTIMATE || above < EDGE_MARGIN) {
        top = Math.min(below, vh - MENU_HEIGHT_ESTIMATE - EDGE_MARGIN);
    } else {
        top = Math.max(EDGE_MARGIN, above);
    }
    top = Math.max(EDGE_MARGIN, top);

    return { left, top, width };
}

export default function LeadShareButton({ item, kind = "registrations", variant = "compact" }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [pos, setPos] = useState({ left: 0, top: 0, width: MENU_WIDTH });
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    // Close on outside click / Esc
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (rootRef.current && rootRef.current.contains(e.target)) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("touchstart", onDoc, { passive: true });
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("touchstart", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // Recompute position on open + on resize / scroll so an orientation
    // change or long-scroll never leaves the menu off-screen.
    useLayoutEffect(() => {
        if (!open) return undefined;
        const recompute = () => {
            if (!triggerRef.current) return;
            setPos(_computePosition(triggerRef.current.getBoundingClientRect()));
        };
        recompute();
        window.addEventListener("resize", recompute);
        window.addEventListener("scroll", recompute, true);
        return () => {
            window.removeEventListener("resize", recompute);
            window.removeEventListener("scroll", recompute, true);
        };
    }, [open]);

    if (!item) return null;

    const shareText = buildLeadShareText(item, kind);
    const chatPrompt = buildChatGPTPrompt(item, kind);

    const doWhatsApp = () => {
        openWhatsAppShare(shareText);
        setOpen(false);
    };
    const doChatGPT = () => {
        openChatGPTShare(chatPrompt);
        setOpen(false);
    };
    const doCopy = async () => {
        const ok = await copyLeadText(shareText);
        if (ok) {
            setCopied(true);
            toast.success("Lead details copied");
            setTimeout(() => setCopied(false), 1500);
        } else {
            toast.error("Could not copy — please try again");
        }
    };

    const triggerClass = variant === "iconOnly"
        ? "flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg active:bg-cyan-50 text-cyan-700"
        : "inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:border-cyan-300 hover:bg-cyan-50/50";

    return (
        <div ref={rootRef} className="relative inline-block" data-testid="lead-share-root">
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                data-testid="lead-share-btn"
                className={triggerClass}
            >
                {variant === "iconOnly" ? (
                    <>
                        <Share2 className="w-5 h-5" />
                        <span className="text-[10px] font-semibold">Share</span>
                    </>
                ) : (
                    <>
                        <Share2 className="w-3.5 h-3.5" /> Share Lead
                    </>
                )}
            </button>

            {open && (
                <div
                    ref={menuRef}
                    role="menu"
                    data-testid="lead-share-menu"
                    className="bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden"
                    style={{
                        // `fixed` + viewport-clamped left/top guarantees the
                        // menu escapes any parent `overflow-hidden` (the
                        // modal shell) AND never crosses a viewport edge.
                        // z-index 90 > mobile sticky action bar (~50) and
                        // the modal chrome (~60), so the menu always floats
                        // on top of both.
                        position: "fixed",
                        left: `${pos.left}px`,
                        top: `${pos.top}px`,
                        width: `${pos.width}px`,
                        maxHeight: "calc(100vh - 16px)",
                        overflowY: "auto",
                        zIndex: 90,
                    }}
                >
                    <button
                        type="button"
                        onClick={doWhatsApp}
                        data-testid="lead-share-whatsapp"
                        className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-emerald-50 active:bg-emerald-100 text-left"
                    >
                        <span className="w-8 h-8 rounded-lg bg-[#25D366] flex items-center justify-center shrink-0">
                            <MessageCircle className="w-4 h-4 text-white" />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm font-semibold text-slate-800">WhatsApp Business</span>
                            <span className="block text-[11px] text-slate-500">Open with formatted message</span>
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={doChatGPT}
                        data-testid="lead-share-chatgpt"
                        className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-violet-50 active:bg-violet-100 text-left border-t border-slate-100"
                    >
                        <span className="w-8 h-8 rounded-lg bg-[#10a37f] flex items-center justify-center shrink-0">
                            <Sparkles className="w-4 h-4 text-white" />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm font-semibold text-slate-800">ChatGPT</span>
                            <span className="block text-[11px] text-slate-500">Draft a reply / follow-up</span>
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={doCopy}
                        data-testid="lead-share-copy"
                        className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 active:bg-slate-100 text-left border-t border-slate-100"
                    >
                        <span className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center shrink-0">
                            {copied ? <Check className="w-4 h-4 text-white" /> : <Copy className="w-4 h-4 text-white" />}
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm font-semibold text-slate-800">
                                {copied ? "Copied!" : "Copy details"}
                            </span>
                            <span className="block text-[11px] text-slate-500">Paste anywhere — email, notes, chat</span>
                        </span>
                    </button>
                </div>
            )}
        </div>
    );
}
